import React, { useEffect, useMemo, useState } from "react";
import supabase from "../../../../conndb";
import NavabarGest from "./navabarGes";
import Loader from "./loader";

export default function Ges_clienti() {
  const [clienti, setClienti] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClienteId, setSelectedClienteId] = useState(null);

  const [editCliente, setEditCliente] = useState(null);
  const [editDevice, setEditDevice] = useState(null);
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState(null); // { type, msg }

  const [showAddCliente, setShowAddCliente] = useState(false);
  const [newCliente, setNewCliente] = useState({
    nome: "",
    cognome: "",
    paese: "",
    indirizzo: "",
    telefono: "",
  });

  const [addClienteError, setAddClienteError] = useState("");
  const [addClienteSubmitting, setAddClienteSubmitting] = useState(false);

  const [showAddDevice, setShowAddDevice] = useState(false);
  const [clienteForDevice, setClienteForDevice] = useState(null);
  const [newDevice, setNewDevice] = useState({
    matricola: "",
    modello: "",
    scadenza: "",
    tipo: "stufe",
  });

  const clearToastLater = () => setTimeout(() => setToast(null), 4000);

  const fetchClienti = async () => {
    setLoading(true);
    setToast(null);

    const { data: clientiData, error: clientiErr } = await supabase.from("clienti").select("*");
    const { data: caldaieData, error: caldaieErr } = await supabase.from("caldaie").select("*");
    const { data: stufeData, error: stufeErr } = await supabase.from("stufe").select("*");

    if (clientiErr || caldaieErr || stufeErr) {
      console.error(clientiErr || caldaieErr || stufeErr);
      setToast({ type: "error", msg: "Errore durante il recupero dei dati." });
      clearToastLater();
      setLoading(false);
      return;
    }

    const clientiConDispositivi = (clientiData || []).map((cliente) => ({
      ...cliente,
      caldaie: (caldaieData || []).filter((c) => c.cliente_id === cliente.id),
      stufe: (stufeData || []).filter((s) => s.cliente_id === cliente.id),
    }));

    setClienti(clientiConDispositivi);
    setLoading(false);
  };

  useEffect(() => {
    fetchClienti();
  }, []);

  const selectedCliente = useMemo(
    () => clienti.find((c) => c.id === selectedClienteId) || null,
    [clienti, selectedClienteId]
  );

  const getNextClienteId = async () => {
    const { data, error } = await supabase
      .from("clienti")
      .select("id")
      .order("id", { ascending: false })
      .limit(1);

    if (error) throw error;

    const maxId = data?.[0]?.id ?? 0;
    return Number(maxId) + 1;
  };

  const handleAddCliente = async () => {
    setAddClienteError("");

    const payload = {
      nome: (newCliente.nome || "").trim(),
      cognome: (newCliente.cognome || "").trim(),
      paese: (newCliente.paese || "").trim(),
      indirizzo: (newCliente.indirizzo || "").trim(),
      telefono: (newCliente.telefono || "").trim(),
    };

    const missing = Object.entries(payload).filter(([_, v]) => !v);
    if (missing.length) {
      setAddClienteError("Compila tutti i campi (sono obbligatori).");
      return;
    }

    setAddClienteSubmitting(true);

    try {
      const nextId = await getNextClienteId();
      const { error } = await supabase.from("clienti").insert([{ id: nextId, ...payload }]);

      if (error) {
        console.error("Insert cliente error:", error);
        if (error.code === "23505") {
          setAddClienteError(
            "Errore: esiste già un record con gli stessi dati (vincolo UNIQUE / duplicato)."
          );
        } else {
          setAddClienteError(`Errore inserimento cliente: ${error.message || "operazione fallita"}`);
        }
        return;
      }

      setShowAddCliente(false);
      setNewCliente({ nome: "", cognome: "", paese: "", indirizzo: "", telefono: "" });
      setAddClienteError("");
      await fetchClienti();

      setToast({ type: "success", msg: "Cliente inserito correttamente." });
      clearToastLater();
    } catch (e) {
      console.error(e);
      setAddClienteError(`Errore inatteso: ${e.message}`);
    } finally {
      setAddClienteSubmitting(false);
    }
  };

  const handleSaveCliente = async () => {
    if (!editCliente?.id) return;

    setToast(null);
    const { id, caldaie, stufe, ...data } = editCliente;

    const payload = {
      ...data,
      telefono: (data.telefono || "").trim(),
      nome: (data.nome || "").trim(),
      cognome: (data.cognome || "").trim(),
      paese: (data.paese || "").trim(),
      indirizzo: (data.indirizzo || "").trim(),
    };

    const { error } = await supabase.from("clienti").update(payload).eq("id", id);

    if (error) {
      console.error("Update cliente error:", error);
      setToast({ type: "error", msg: `Errore modifica cliente: ${error.message}` });
      clearToastLater();
      return;
    }

    setToast({ type: "success", msg: "Cliente aggiornato correttamente." });
    clearToastLater();

    setEditCliente(null);
    fetchClienti();
  };

  const handleAddDeviceToCliente = async () => {
    setToast(null);

    if (!newDevice.matricola || !newDevice.modello) {
      setToast({ type: "error", msg: "Compila matricola e modello." });
      clearToastLater();
      return;
    }

    const { error } = await supabase.from(newDevice.tipo).insert([
      {
        cliente_id: clienteForDevice.id,
        matricola: (newDevice.matricola || "").trim(),
        modello: (newDevice.modello || "").trim(),
        scadenza: newDevice.scadenza,
      },
    ]);

    if (error) {
      console.error("Insert device error:", error);
      setToast({ type: "error", msg: `Errore inserimento dispositivo: ${error.message}` });
      clearToastLater();
      return;
    }

    setToast({ type: "success", msg: "Dispositivo associato correttamente." });
    clearToastLater();

    setShowAddDevice(false);
    setClienteForDevice(null);
    setNewDevice({ matricola: "", modello: "", scadenza: "", tipo: "stufe" });
    fetchClienti();
  };

  const handleSaveDevice = async () => {
    if (!editDevice?.id) return;

    setToast(null);

    const { id, type, cliente_id, ...data } = editDevice;
    const payload = {
      ...data,
      matricola: (data.matricola || "").trim(),
      modello: (data.modello || "").trim(),
    };

    const { error } = await supabase.from(type).update(payload).eq("id", id);

    if (error) {
      console.error("Update device error:", error);
      setToast({ type: "error", msg: `Errore modifica dispositivo: ${error.message}` });
      clearToastLater();
      return;
    }

    setToast({ type: "success", msg: "Dispositivo aggiornato correttamente." });
    clearToastLater();

    setEditDevice(null);
    fetchClienti();
  };

  const handleDeleteCliente = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) return;

    setToast(null);
    const { error } = await supabase.from("clienti").delete().eq("id", id);

    if (error) {
      console.error("Delete cliente error:", error);
      setToast({ type: "error", msg: `Errore eliminazione cliente: ${error.message}` });
      clearToastLater();
      return;
    }

    setToast({ type: "success", msg: "Cliente eliminato." });
    clearToastLater();

    // se stai visualizzando quel cliente, chiudi la vista
    if (selectedClienteId === id) setSelectedClienteId(null);

    fetchClienti();
  };

  const handleDeleteDevice = async (table, id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo dispositivo?")) return;

    setToast(null);
    const { error } = await supabase.from(table).delete().eq("id", id);

    if (error) {
      console.error("Delete device error:", error);
      setToast({ type: "error", msg: `Errore eliminazione dispositivo: ${error.message}` });
      clearToastLater();
      return;
    }

    setToast({ type: "success", msg: "Dispositivo eliminato." });
    clearToastLater();
    fetchClienti();
  };

  const filteredClienti = useMemo(() => {
    const term = search.toLowerCase();
    return clienti.filter((c) => {
      return (
        (c.nome || "").toString().toLowerCase().includes(term) ||
        (c.cognome || "").toString().toLowerCase().includes(term) ||
        (c.paese || "").toString().toLowerCase().includes(term) ||
        (c.indirizzo || "").toString().toLowerCase().includes(term) ||
        (c.telefono || "").toString().toLowerCase().includes(term)
      );
    });
  }, [clienti, search]);

  const allDevicesOf = (cliente) => [
    ...(cliente?.caldaie || []).map((d) => ({ ...d, type: "caldaie" })),
    ...(cliente?.stufe || []).map((d) => ({ ...d, type: "stufe" })),
  ];

  if (loading) return <Loader />;

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <NavabarGest />

      <div className="p-4 bg-[#5b90ab70] rounded-2xl">
        <h1 className="font-bold mb-4 text-center text-2xl md:text-3xl text-white">
          Gestione Clienti
        </h1>

        {toast && (
          <div
            className={`mb-4 rounded-lg px-4 py-2 ${
              toast.type === "success"
                ? "bg-green-600 text-white"
                : toast.type === "info"
                ? "bg-blue-600 text-white"
                : "bg-red-600 text-white"
            }`}
          >
            {toast.msg}
          </div>
        )}

        {/* Search + Inserisci */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-4">
          <input
            type="text"
            placeholder="Cerca cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="p-2 rounded-2xl w-full bg-[#173341] text-white"
          />
          <button
            onClick={() => {
              setToast(null);
              setAddClienteError("");
              setShowAddCliente(true);
            }}
            className="bg-green-500 text-white px-4 py-2 rounded-2xl font-bold whitespace-nowrap"
          >
            Inserisci Cliente
          </button>
        </div>

        {/* DESKTOP/TABLET: TABELLA */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-white bg-[#173341] rounded-2xl overflow-hidden">
              <thead className="bg-[#0f2430]">
                <tr className="text-left">
                  <th className="p-3">Nome</th>
                  <th className="p-3">Cognome</th>
                  <th className="p-3">Telefono</th>
                  <th className="p-3">Indirizzo</th>
                  <th className="p-3">Paese</th>
                  <th className="p-3 text-center">Caldaie</th>
                  <th className="p-3 text-center">Stufe</th>
                  <th className="p-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredClienti.map((c) => (
                  <tr key={c.id} className="border-t border-white/10">
                    <td className="p-3">{c.nome}</td>
                    <td className="p-3">{c.cognome}</td>
                    <td className="p-3">{c.telefono}</td>
                    <td className="p-3 max-w-[360px] truncate" title={c.indirizzo || ""}>
                      {c.indirizzo}
                    </td>
                    <td className="p-3">{c.paese}</td>
                    <td className="p-3 text-center">{(c.caldaie || []).length}</td>
                    <td className="p-3 text-center">{(c.stufe || []).length}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => setSelectedClienteId(c.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-xl"
                      >
                        Visualizza
                      </button>
                    </td>
                  </tr>
                ))}

                {!filteredClienti.length && (
                  <tr>
                    <td className="p-4 text-center text-white/80" colSpan={8}>
                      Nessun cliente trovato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBILE: LISTA SEMPLICE */}
        <div className="md:hidden space-y-3">
          {filteredClienti.map((c) => (
            <div key={c.id} className="bg-[#173341] rounded-2xl p-4 flex items-center justify-between">
              <div className="text-white">
                <div className="font-bold">{c.nome} {c.cognome}</div>
              </div>
              <button
                onClick={() => setSelectedClienteId(c.id)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-xl text-sm"
              >
                Visualizza
              </button>
            </div>
          ))}

          {!filteredClienti.length && (
            <div className="bg-[#173341] rounded-2xl p-4 text-white/80 text-center">
              Nessun cliente trovato.
            </div>
          )}
        </div>
      </div>

      {/* VISTA DETTAGLIO "PAGINA" FULLSCREEN */}
      {selectedCliente && (
        <div className="fixed inset-0 z-50 bg-[#173341] overflow-y-auto">
          <div className="p-4 md:p-6">
            <NavabarGest />

            {/* Header dettaglio + X */}
            <div className="mt-4 bg-[#5b90ab70] rounded-2xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="text-white">
                  <h2 className="text-xl md:text-2xl font-bold">
                    Cliente: {selectedCliente.nome} {selectedCliente.cognome}
                  </h2>
                  <p className="text-white/80 text-sm md:text-base">
                    Dettaglio cliente e dispositivi associati
                  </p>
                </div>

                <button
                  onClick={() => setSelectedClienteId(null)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-xl px-3 py-2"
                  aria-label="Chiudi"
                  title="Chiudi"
                >
                  ✕
                </button>
              </div>

              {/* Azioni cliente */}
              <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setToast(null);
                    setEditCliente(selectedCliente);
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-2xl font-semibold"
                >
                  Modifica
                </button>

                <button
                  onClick={() => handleDeleteCliente(selectedCliente.id)}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-2xl font-semibold"
                >
                  Elimina
                </button>

                <button
                  onClick={() => {
                    setToast(null);
                    setClienteForDevice(selectedCliente);
                    setShowAddDevice(true);
                  }}
                  className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-2xl font-semibold"
                >
                  Associa Dispositivo
                </button>
              </div>

              {/* Dati cliente */}
              <div className="mt-4 bg-[#173341] rounded-2xl p-4">
                <h3 className="text-white font-bold mb-3">Dati cliente</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InfoBox label="Nome" value={selectedCliente.nome} />
                  <InfoBox label="Cognome" value={selectedCliente.cognome} />
                  <InfoBox label="Telefono" value={selectedCliente.telefono} />
                  <InfoBox label="Paese" value={selectedCliente.paese} />
                  <div className="md:col-span-2">
                    <InfoBox label="Indirizzo" value={selectedCliente.indirizzo} />
                  </div>
                </div>
              </div>

              {/* Dispositivi */}
              <div className="mt-4 bg-[#173341] rounded-2xl p-4">
                <h3 className="text-white font-bold mb-3">Dispositivi</h3>

                <div className="space-y-3">
                  {allDevicesOf(selectedCliente).length === 0 && (
                    <div className="text-white/80">Nessun dispositivo associato.</div>
                  )}

                  {allDevicesOf(selectedCliente).map((dev) => (
                    <div
                      key={`${dev.id}-${dev.type}`}
                      className="bg-[#5b90ab70] rounded-2xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                    >
                      <div className="text-white text-sm md:text-base">
                        <p><strong>Categoria:</strong> {dev.type}</p>
                        <p className="break-words"><strong>Matricola:</strong> {dev.matricola}</p>
                        <p className="break-words"><strong>Modello:</strong> {dev.modello}</p>
                        <p><strong>Scadenza:</strong> {dev.scadenza}</p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => {
                            setToast(null);
                            setEditDevice(dev);
                          }}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-2xl"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => handleDeleteDevice(dev.type, dev.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-2xl"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Toast anche nel dettaglio (comodo su mobile) */}
              {toast && (
                <div
                  className={`mt-4 rounded-lg px-4 py-2 ${
                    toast.type === "success"
                      ? "bg-green-600 text-white"
                      : toast.type === "info"
                      ? "bg-blue-600 text-white"
                      : "bg-red-600 text-white"
                  }`}
                >
                  {toast.msg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODALE: Aggiungi Cliente */}
      {showAddCliente && (
        <div className="fixed inset-0 z-[60] bg-[#000000ba] flex items-center justify-center p-4">
          <div className="bg-[#173341] p-6 rounded-xl w-full max-w-md text-white">
            <h2 className="text-xl font-bold mb-4 text-center">Nuovo Cliente</h2>

            {addClienteError && (
              <div className="mb-3 rounded-lg bg-red-600 text-white px-4 py-2">
                {addClienteError}
              </div>
            )}

            {["nome", "cognome", "paese", "indirizzo", "telefono"].map((field) => (
              <input
                key={field}
                type="text"
                required
                placeholder={`${field.charAt(0).toUpperCase() + field.slice(1)} *`}
                value={newCliente[field]}
                onChange={(e) => {
                  setAddClienteError("");
                  setNewCliente({ ...newCliente, [field]: e.target.value });
                }}
                className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
              />
            ))}

            <div className="flex justify-end space-x-2 mt-3">
              <button
                onClick={() => {
                  setShowAddCliente(false);
                  setAddClienteError("");
                }}
                className="px-3 py-2 rounded bg-red-600"
                disabled={addClienteSubmitting}
              >
                Annulla
              </button>
              <button
                onClick={handleAddCliente}
                className="px-3 py-2 rounded bg-green-500 text-white"
                disabled={addClienteSubmitting}
              >
                {addClienteSubmitting ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Modifica Cliente */}
      {editCliente && (
        <div className="fixed inset-0 z-[60] bg-[#000000ba] flex items-center justify-center p-4 text-white">
          <div className="bg-[#173341] p-6 rounded-xl w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-center">Modifica Cliente</h2>

            {["nome", "cognome", "paese", "indirizzo", "telefono"].map((field) => (
              <input
                key={field}
                type="text"
                placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                value={editCliente[field]}
                onChange={(e) => setEditCliente({ ...editCliente, [field]: e.target.value })}
                className="mb-2 p-2 bg-[#5b90ab70] rounded w-full"
              />
            ))}

            <div className="flex justify-end space-x-2 mt-3">
              <button
                onClick={() => setEditCliente(null)}
                className="px-3 py-2 rounded bg-red-600"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveCliente}
                className="px-3 py-2 rounded bg-blue-500 text-white"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Aggiungi Dispositivo */}
      {showAddDevice && clienteForDevice && (
        <div className="fixed inset-0 z-[60] bg-[#000000ba] flex items-center justify-center p-4">
          <div className="bg-[#173341] p-6 rounded-xl w-full max-w-md text-white">
            <h2 className="text-xl font-bold mb-4 text-center">
              Associa Dispositivo a {clienteForDevice.nome} {clienteForDevice.cognome}
            </h2>

            <input
              type="text"
              placeholder="Matricola"
              value={newDevice.matricola}
              onChange={(e) => setNewDevice({ ...newDevice, matricola: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            />
            <input
              type="text"
              placeholder="Modello"
              value={newDevice.modello}
              onChange={(e) => setNewDevice({ ...newDevice, modello: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            />
            <input
              type="date"
              value={newDevice.scadenza}
              onChange={(e) => setNewDevice({ ...newDevice, scadenza: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            />
            <select
              value={newDevice.tipo}
              onChange={(e) => setNewDevice({ ...newDevice, tipo: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            >
              <option value="stufe">Stufa</option>
              <option value="caldaie">Caldaia</option>
            </select>

            <div className="flex justify-end space-x-2 mt-3">
              <button
                onClick={() => {
                  setShowAddDevice(false);
                  setClienteForDevice(null);
                }}
                className="px-3 py-2 rounded bg-red-600"
              >
                Annulla
              </button>
              <button
                onClick={handleAddDeviceToCliente}
                className="px-3 py-2 rounded bg-green-500 text-white"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Modifica Dispositivo */}
      {editDevice && (
        <div className="fixed inset-0 z-[60] bg-[#000000ba] flex items-center justify-center p-4">
          <div className="bg-[#173341] p-6 rounded-xl w-full max-w-md text-white">
            <h2 className="text-xl font-bold mb-4">Modifica Dispositivo</h2>

            <input
              type="text"
              placeholder="Matricola"
              value={editDevice.matricola}
              onChange={(e) => setEditDevice({ ...editDevice, matricola: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            />
            <input
              type="text"
              placeholder="Modello"
              value={editDevice.modello}
              onChange={(e) => setEditDevice({ ...editDevice, modello: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            />
            <input
              type="date"
              value={editDevice.scadenza}
              onChange={(e) => setEditDevice({ ...editDevice, scadenza: e.target.value })}
              className="mb-2 p-2 rounded w-full bg-[#5b90ab70]"
            />

            <div className="flex justify-end space-x-2 mt-3">
              <button
                onClick={() => setEditDevice(null)}
                className="px-3 py-2 rounded bg-red-600"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveDevice}
                className="px-3 py-2 rounded bg-blue-500 text-white"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="p-3 bg-[#5b90ab70] rounded-2xl flex flex-col gap-1">
      <div className="text-white/90 text-xs font-semibold">{label}</div>
      <div className="text-white break-words">{value || "-"}</div>
    </div>
  );
}
